import { useCallback, useState } from "react";
import { Link } from "react-router-dom";
import Layout from "../layout/Layout";
import SEO from "../Components/SEO/SEO";
import ApplicationForm from "../Components/careers/ApplicationForm";
import { Button } from "../Components/ui/Button";
import { ArrowRight, Briefcase, MapPin } from "lucide-react";

const openRoles = [
  {
    title: "React / Full Stack Developer",
    type: "Remote-friendly",
    description:
      "Build production React and Next.js applications — APIs, dashboards, and customer-facing web apps with clean architecture and code review standards.",
  },
  {
    title: "UI/UX Designer",
    type: "Remote-friendly",
    description:
      "Own user research, wireframes, and high-fidelity UI for web and mobile products. Experience with design systems and developer handoff is essential.",
  },
  {
    title: "QA Engineer",
    type: "Remote-friendly",
    description:
      "Design test plans, run manual and automated regression, and partner with engineering before every release to keep launches predictable.",
  },
  {
    title: "AI Developer",
    type: "Remote-friendly",
    description:
      "Integrate LLMs, retrieval pipelines, and automation into real products with monitoring, cost controls, and UX that keeps humans in the loop.",
  },
  {
    title: "Business Development Representative",
    type: "Remote-friendly",
    description:
      "Identify partnership and client opportunities, qualify inbound leads, and support proposals for web, mobile, and AI engagements.",
  },
  {
    title: "HR / People Operations",
    type: "Remote-friendly",
    description:
      "Own recruiting pipelines, candidate experience, onboarding, and people operations for a growing software team — from role posts to interview coordination and culture support.",
  },
];

const benefits = [
  "Real client projects across SaaS, e-commerce, mobile, and AI",
  "Senior-led architecture and mentorship",
  "Transparent sprints with weekly demos",
  "Remote-friendly collaboration",
  "Code and documentation standards you can grow with",
];

export default function CareersPage() {
  const [selectedRole, setSelectedRole] = useState("");

  const clearSelectedRole = useCallback(() => setSelectedRole(""), []);

  const applyForRole = (title) => {
    setSelectedRole(title);
    requestAnimationFrame(() => {
      document.getElementById("apply")?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };

  return (
    <Layout>
      <SEO
        title="Careers | Join Quantum Spark Technology"
        description="Apply online to Quantum Spark Technology. Upload your resume for React, UI/UX, QA, AI, and business development roles. Remote-friendly."
        path="/careers"
      />

      <section className="border-b border-zinc-200 bg-[#FAFAFB] py-16 md:py-20">
        <div className="mx-auto max-w-7xl px-6">
          <p className="mb-4 text-sm font-semibold uppercase tracking-widest text-brand-purple">Careers</p>
          <h1 className="mb-6 max-w-3xl font-display text-4xl font-extrabold tracking-tight text-brand-ink md:text-5xl">
            Join a team that ships real software
          </h1>
          <p className="mb-8 max-w-2xl text-lg leading-relaxed text-brand-muted">
            Quantum Spark Technology is growing. We look for people who care about quality, honest communication, and
            building products founders can rely on — not endless prototypes that never launch.
          </p>
          <Button
            className="h-12 rounded-full bg-brand-purple px-8 text-white hover:bg-brand-purple-dark"
            onClick={() => {
              document.getElementById("apply")?.scrollIntoView({ behavior: "smooth", block: "start" });
            }}
          >
            Apply now <ArrowRight className="ml-1 h-4 w-4" />
          </Button>
        </div>
      </section>

      <section className="bg-white py-16">
        <div className="mx-auto max-w-7xl px-6">
          <h2 className="mb-8 font-display text-2xl font-bold text-brand-ink">Why work with us</h2>
          <ul className="mb-16 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {benefits.map((item) => (
              <li
                key={item}
                className="rounded-2xl border border-zinc-200 bg-[#FAFAFB] p-5 text-sm font-medium text-zinc-700"
              >
                {item}
              </li>
            ))}
          </ul>

          <h2 className="mb-8 font-display text-2xl font-bold text-brand-ink">Open roles</h2>
          <div className="mb-16 space-y-6">
            {openRoles.map((role) => (
              <article
                key={role.title}
                className="flex flex-col gap-4 rounded-3xl border border-zinc-200 p-6 md:flex-row md:items-center md:justify-between md:p-8"
              >
                <div className="min-w-0 flex-1">
                  <div className="mb-3 flex flex-wrap items-center gap-3">
                    <Briefcase size={20} className="text-brand-purple" />
                    <h3 className="font-display text-xl font-bold text-brand-ink">{role.title}</h3>
                    <span className="rounded-full bg-violet-50 px-3 py-1 text-xs font-semibold text-brand-purple">
                      {role.type}
                    </span>
                  </div>
                  <p className="text-brand-muted">{role.description}</p>
                </div>
                <Button
                  className="h-11 shrink-0 rounded-full bg-brand-purple px-6 text-white hover:bg-brand-purple-dark"
                  onClick={() => applyForRole(role.title)}
                >
                  Apply
                </Button>
              </article>
            ))}
          </div>

          <div className="mx-auto max-w-3xl">
            <ApplicationForm
              roles={openRoles}
              selectedRole={selectedRole}
              onRoleConsumed={clearSelectedRole}
            />
          </div>

          <p className="mt-10 text-center text-sm text-brand-muted">
            Not applying for a job?{" "}
            <Link to="/contact" className="font-medium text-brand-purple hover:underline">
              Contact us about a project
            </Link>
            .
          </p>
        </div>
      </section>

      <section className="border-t border-zinc-200 bg-[#FAFAFB] py-12">
        <div className="mx-auto flex max-w-7xl items-start gap-3 px-6 text-sm text-brand-muted">
          <MapPin size={18} className="mt-0.5 shrink-0 text-brand-purple" />
          <p>
            We collaborate with teammates worldwide. Roles are remote-friendly unless a specific client engagement
            requires otherwise.
          </p>
        </div>
      </section>
    </Layout>
  );
}
